import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import useAuthStore from "../../../store/useAuthStore";
import { PRIORITY_LEVELS } from "../../../constants/inventory";
import { IoArrowBack } from "react-icons/io5";
import { MdAdd, MdDelete } from "react-icons/md";

const NewRequisition = () => {
  const { token } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [items, setItems] = useState([{ item: "", qty: 1, purpose: "" }]);
  const [priority, setPriority] = useState("medium");
  const [project, setProject] = useState("");
  const [department, setDepartment] = useState("");
  const [notes, setNotes] = useState("");

  const [inventoryItems, setInventoryItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [departments, setDepartments] = useState([]);

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    fetchDropdownData();
  }, []);

  // Pre-populate item from catalog "Request This Item" link
  useEffect(() => {
    const itemId = searchParams.get("itemId");
    if (itemId) {
      setItems([{ item: itemId, qty: 1, purpose: "" }]);
    }
  }, [searchParams]);

  const fetchDropdownData = async () => {
    try {
      const [itemsRes, projectsRes, deptsRes] = await Promise.all([
        fetch("/api/getAllInventoryItems?limit=100", {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch("/api/getAllProjects?limit=100", {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch("/api/getAllDepartments", {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const itemsData = await itemsRes.json();
      const projectsData = await projectsRes.json();
      const deptsData = await deptsRes.json();

      if (itemsRes.ok) setInventoryItems(itemsData.items || []);
      if (projectsRes.ok) setProjects(projectsData.projects || []);
      if (deptsRes.ok) setDepartments(deptsData.departments || deptsData || []);
    } catch (err) {
      console.error("Error fetching dropdown data:", err);
    }
  };

  const addLineItem = () => {
    setItems([...items, { item: "", qty: 1, purpose: "" }]);
  };

  const removeLineItem = (index) => {
    if (items.length === 1) return;
    setItems(items.filter((_, i) => i !== index));
  };

  const updateLineItem = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;
    setItems(updated);
  };

  const getEstimatedCost = () => {
    return items.reduce((total, lineItem) => {
      const invItem = inventoryItems.find((i) => i._id === lineItem.item);
      return total + (invItem ? invItem.unitCost * lineItem.qty : 0);
    }, 0);
  };

  const validateForm = () => {
    if (items.some((i) => !i.item || i.qty < 1)) {
      setErrorMessage("Each item must be selected with quantity >= 1");
      return false;
    }
    return true;
  };

  const handleSubmit = async (asDraft = true) => {
    if (!validateForm()) {
      setTimeout(() => setErrorMessage(""), 3000);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch("/api/addRequisition", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          items: items.map((i) => ({
            item: i.item,
            qty: parseInt(i.qty),
            purpose: i.purpose,
          })),
          priority,
          project: project || undefined,
          department: department || undefined,
          notes,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        setErrorMessage(data.error || "Failed to create requisition");
        setTimeout(() => setErrorMessage(""), 3000);
        return;
      }

      // If not saving as draft, submit for approval
      if (!asDraft && data.requisition?._id) {
        await fetch(`/api/requisition/submit/${data.requisition._id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
      }

      setSuccessMessage(
        asDraft ? "Requisition saved as draft" : "Requisition submitted for approval"
      );
      setTimeout(() => {
        navigate("/Requisitions");
      }, 1500);
    } catch (err) {
      setErrorMessage("Failed to create requisition");
      setTimeout(() => setErrorMessage(""), 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="m-2 md:m-6 p-2 md:p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate("/Requisitions")}
          className="p-2 rounded-full hover:bg-gray-100"
        >
          <IoArrowBack className="text-xl text-primary" />
        </button>
        <h2 className="text-xl font-semibold text-gray-800">New Requisition</h2>
      </div>

      {/* Messages */}
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {errorMessage}
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
          {successMessage}
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-6">
        {/* Priority, Project, Department row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block font-medium mb-2 text-blue-500">
              Priority
            </label>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {PRIORITY_LEVELS.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block font-medium mb-2 text-blue-500">
              Project (Optional)
            </label>
            <select
              value={project}
              onChange={(e) => setProject(e.target.value)}
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Project</option>
              {projects.map((p) => (
                <option key={p._id} value={p._id}>
                  {p.projectCode} - {p.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block font-medium mb-2 text-blue-500">
              Department
            </label>
            <select
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Department</option>
              {departments.map((d) => (
                <option key={d._id} value={d._id}>{d.deptName}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Line Items */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-gray-700">
              Items <span className="text-red-500">*</span>
            </label>
            <button
              onClick={addLineItem}
              className="flex items-center gap-1 text-primary text-sm font-medium hover:underline"
            >
              <MdAdd /> Add Item
            </button>
          </div>

          <div className="border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">#</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Item</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Qty</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Available</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Unit Cost</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Purpose</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((lineItem, index) => {
                  const selectedItem = inventoryItems.find((i) => i._id === lineItem.item);
                  return (
                    <tr key={index} className="border-t">
                      <td className="px-4 py-2 text-sm text-gray-500">{index + 1}</td>
                      <td className="px-4 py-2">
                        <select
                          value={lineItem.item}
                          onChange={(e) => updateLineItem(index, "item", e.target.value)}
                          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                        >
                          <option value="">Select item</option>
                          {inventoryItems.map((inv) => (
                            <option key={inv._id} value={inv._id}>
                              {inv.sku} - {inv.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          min="1"
                          value={lineItem.qty}
                          onChange={(e) => updateLineItem(index, "qty", e.target.value)}
                          className="w-20 border border-gray-300 rounded px-2 py-1.5 text-sm"
                        />
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-500">
                        {selectedItem ? selectedItem.currentStock : "-"}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-500">
                        {selectedItem ? `Nu. ${selectedItem.unitCost}` : "-"}
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="text"
                          placeholder="Purpose"
                          value={lineItem.purpose}
                          onChange={(e) => updateLineItem(index, "purpose", e.target.value)}
                          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                        />
                      </td>
                      <td className="px-4 py-2">
                        {items.length > 1 && (
                          <button
                            onClick={() => removeLineItem(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <MdDelete />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end mt-2">
            <p className="text-sm font-medium text-gray-700">
              Estimated Total: <span className="text-primary">Nu. {getEstimatedCost().toLocaleString()}</span>
            </p>
          </div>
        </div>

        {/* Notes */}
        <div className="mb-6">
          <label className="block font-medium mb-2 text-blue-500">
            Notes / Justification
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Why do you need these items?"
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            onClick={() => navigate("/Requisitions")}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => handleSubmit(true)}
            disabled={loading}
            className="px-4 py-2 border border-primary text-primary rounded-lg text-sm hover:bg-blue-50 disabled:opacity-50"
          >
            {loading ? "Saving..." : "Save as Draft"}
          </button>
          <button
            onClick={() => handleSubmit(false)}
            disabled={loading}
            className="px-4 py-2 bg-primary text-white rounded-lg text-sm hover:opacity-90 disabled:opacity-50"
          >
            {loading ? "Submitting..." : "Submit for Approval"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewRequisition;
